import type { EndingDef, SceneLogEntry } from '@plotjack/core';
import { trackLabel } from '@plotjack/core';
import { BOOK1_STORY } from '../data/story';
import { backgroundUrl } from '../art/assets';
import { NarratorPortrait } from './NarratorPortrait';

const ENDING_ART: Record<string, [string, string, string]> = {
  ending_hope: ['s11_finale', 'calm', 'flustered'],
  ending_dread: ['s11_finale', 'dread', 'smug'],
  ending_chaos: ['s11_finale', 'chaos', 'panicked'],
  ending_fate: ['s10_choice', 'calm', 'wary'],
};

interface Props {
  ending: EndingDef | undefined;
  dread: number;
  tracks: Record<string, number>;
  playedCount: number;
  log?: SceneLogEntry[];
  chosenEndingId?: string;
  /** meter value the chosen ending needed in this run (scaled by run length) */
  chosenTarget?: number;
  onTitle: () => void;
}

export function RecapScreen({ ending, dread, tracks, playedCount, log = [], chosenEndingId, chosenTarget, onTitle }: Props) {
  const chosen = BOOK1_STORY.endings.find((e) => e.id === chosenEndingId);
  const need = chosenTarget ?? chosen?.minValue;
  const why = !ending
    ? ''
    : ending.id === 'ending_dread'
      ? 'Dread reached 10, so Vell’s outline won.'
      : chosen && chosen.id === ending.id
        ? `You reached your chosen ending: ${trackLabel(BOOK1_STORY, chosen.track)} hit ${tracks[chosen.track]} (needed ${need}).`
        : chosen
          ? `Your chosen ending needed ${trackLabel(BOOK1_STORY, chosen.track)} at ${need} (you had ${tracks[chosen.track] ?? 0}), so another ending decided instead.`
          : '';
  return (
    <section className="screen recap-screen" aria-labelledby="recap-heading">
      {(() => {
        const [scene, mood] = ENDING_ART[ending?.id ?? ''] ?? ['s12_epilogue', 'calm'];
        const url = backgroundUrl(scene, mood);
        return url ? (
          <div className="recap-art" aria-hidden="true">
            <img src={url} alt="" onError={(e) => (e.currentTarget.style.display = 'none')} />
          </div>
        ) : null;
      })()}
      <NarratorPortrait expression={ENDING_ART[ending?.id ?? '']?.[2] ?? 'annoyed'} />
      <h1 id="recap-heading">Run Recap</h1>
      <p className="recap-ending">{ending?.title ?? 'The story closed anyway.'}</p>
      <p>{ending?.description}</p>
      {why && <p className="recap-why">{why}</p>}
      <dl className="recap-stats">
        <div>
          <dt>Dread reached</dt>
          <dd>{dread} / 10</dd>
        </div>
        <div>
          <dt>Cards played</dt>
          <dd>{playedCount}</dd>
        </div>
        {(['hope', 'chaos', 'fate'] as const).map((k) => (
          <div key={k}>
            <dt>{trackLabel(BOOK1_STORY, k)}</dt>
            <dd>{tracks[k] ?? 0}</dd>
          </div>
        ))}
      </dl>
      {log.length > 0 && (
        <section className="recap-story" aria-label="The story you told">
          <h2>The story you told</h2>
          <ol>
            {log.map((e) => (
              <li key={e.sceneIndex}>
                <strong>{e.title}.</strong> {e.summary}
                {e.plays.length > 0 && <span className="recap-story__plays"> You {e.plays.map((p) => p.did).join(', then ')}.</span>}
              </li>
            ))}
          </ol>
        </section>
      )}
      <p className="scar-note">A Scar card may now wait in your deck for the next run.</p>
      <button type="button" className="btn btn-primary" onClick={onTitle}>
        Return to Title
      </button>
    </section>
  );
}
